import { Controller } from "@hotwired/stimulus";
import CookieSet from "../lib/cookie_set";

/**
 * Accordion controller.
 *
 * The open/closed state is driven by `aria-expanded` on the button, which is
 * set server-side. CSS handles the chevron rotation based on that attribute.
 *
 * Optional persistence: if the root element has BOTH
 * `data-accordion-cookie-name` and `data-accordion-cookie-value` attributes,
 * collapsed state is stored in the named cookie (via CookieSet) so the server
 * can render the correct initial state on the next visit.
 *
 * Example HTML:
 *
 *   <div class="accordion">
 *     <div class="accordion-item" data-controller="accordion">
 *       <h2 class="accordion-header">
 *         <button class="accordion-button h-stack w-full text-left"
 *                 type="button"
 *                 data-action="click->accordion#toggle"
 *                 data-accordion-target="button"
 *                 aria-expanded="false"
 *                 aria-controls="collapse-1">
 *           <span class="h6">Heading</span>
 *           <span class="accordion-chevron ml-auto">
 *             <%= icon "chevron-down" %>
 *           </span>
 *         </button>
 *       </h2>
 *
 *       <div class="accordion-body" id="collapse-1" data-accordion-target="body">
 *         <p>Content</p>
 *       </div>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["body", "button"];

  connect() {
    this.bodyTarget.hidden = !this.isExpanded();
  }

  toggle() {
    const expanded = !this.isExpanded();
    this.setExpanded(expanded);
    this.persist(expanded);
  }

  isExpanded() {
    return this.buttonTarget.getAttribute("aria-expanded") === "true";
  }

  setExpanded(expanded) {
    this.buttonTarget.setAttribute("aria-expanded", String(expanded));
    this.bodyTarget.hidden = !expanded;
  }

  persist(expanded) {
    const { accordionCookieName, accordionCookieValue } = this.element.dataset;
    if (!accordionCookieName || !accordionCookieValue) return;

    const cookie = new CookieSet(accordionCookieName);
    if (expanded) {
      cookie.remove(accordionCookieValue);
    } else {
      cookie.add(accordionCookieValue);
    }
  }
}
